import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ArrowRight, ArrowLeft, CheckCircle, Phone, MapPin, Home as HomeIcon, Building2, Key, Truck, Map, Users, Award, Shield, Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { companyInfo, services, stats } from '../data/mock';
import { propertyService } from '../services/propertyService';
import { testimonialService } from '../services/testimonialService';
import { toast } from 'sonner';

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formRating, setFormRating] = useState(0);
  const [formComment, setFormComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [testimonialPage, setTestimonialPage] = useState(0);
  const testimonialsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);
  const visibleTestimonials = testimonials.slice(
    testimonialPage * testimonialsPerPage,
    (testimonialPage + 1) * testimonialsPerPage
  );

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        setLoadingProperties(true);
        const response = await propertyService.getAllProperties({ enVedette: true, limit: 6 });
        setFeaturedProperties(response.data || []);
      } catch (error) {
        console.error('Error loading featured properties:', error);
      } finally {
        setLoadingProperties(false);
      }
    };
    loadFeatured();
  }, []);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const data = await testimonialService.getApprovedTestimonials();
        setTestimonials(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading testimonials:', error);
      }
    };
    loadTestimonials();
  }, []);

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    if (formRating === 0) {
      toast.error('Veuillez sélectionner une note');
      return;
    }
    try {
      setSubmitting(true);
      await testimonialService.submitTestimonial({
        nomClient: formName,
        note: formRating,
        commentaire: formComment,
      });
      toast.success('Merci ! Votre témoignage sera publié après validation.');
      setShowTestimonialForm(false);
      setFormName('');
      setFormRating(0);
      setFormComment('');
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error("Erreur lors de l'envoi du témoignage. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.7)), url('https://images.unsplash.com/photo-1613490493576-7fde63acd811')`
          }}
        />
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">
            {companyInfo.name}
          </h1>
          <p className="text-2xl md:text-3xl mb-4 text-amber-400 font-medium">
            {companyInfo.slogan}
          </p>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-slate-200">
            Votre partenaire de confiance pour tous vos projets immobiliers au Sénégal
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8">
              <Link to="/villas">
                Découvrir nos biens
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8">
              <Link to="/contact">Nous contacter</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-amber-400 mb-2">{stat.value}</div>
                <div className="text-slate-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Pourquoi choisir PAM IMMO SERVICES ?
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                {companyInfo.description}
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: Shield, text: 'Sécurité juridique garantie' },
                  { icon: Award, text: 'Expertise locale reconnue' },
                  { icon: Users, text: 'Accompagnement personnalisé' },
                  { icon: CheckCircle, text: 'Transparence et confiance' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="bg-slate-900 hover:bg-slate-800">
                <Link to="/about">
                  En savoir plus
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1756435292384-1bf32eff7baf"
                alt="Villa de luxe"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-600 text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">5+</div>
                <div className="text-sm">Années d'expérience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Nos Services</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une gamme complète de services immobiliers pour répondre à tous vos besoins
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              // Map service names to icons
              const iconMap = {
                'Vente de Terrains': MapPin,
                'Vente de Biens Immobiliers': HomeIcon,
                'Construction Clé en Main': Building2,
                'Gérance Immobilière': Key,
                'Terrassement': Truck,
                'Lotissement': Map
              };
              const Icon = iconMap[service.nomService] || HomeIcon;
              return (
                <Card key={service.idService} className="group hover:shadow-xl transition-all duration-300 border-0 overflow-hidden">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center mb-4 group-hover:bg-amber-600 transition-colors">
                      <Icon className="w-7 h-7 text-amber-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">
                      {service.nomService}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-3">{service.description}</p>
                    <Button asChild variant="link" className="p-0 h-auto text-amber-600 hover:text-amber-700">
                      <Link to="/services">
                        En savoir plus
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700">
              <Link to="/services">Voir tous les services</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Properties Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Biens en vedette</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Découvrez notre sélection de biens immobiliers d'exception
            </p>
          </div>
          {loadingProperties ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, index) => (
                <Card key={index} className="overflow-hidden border-0 shadow-lg">
                  <Skeleton className="h-64 w-full" />
                  <CardContent className="p-6 space-y-4">
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-8 w-1/3" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <Card key={property.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={property.images?.[0]?.urlImage || '/placeholder.jpg'}
                      alt={property.titre}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-amber-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {property.typeBien}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{property.titre}</h3>
                    <div className="flex items-center gap-2 text-slate-600 mb-3">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{property.localisation?.zone}, {property.localisation?.ville}</span>
                    </div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-amber-600">
                        {property.prixSurDemande ? 'Sur demande' : property.prix ? `${parseInt(property.prix).toLocaleString()} ${property.devise || 'FCFA'}` : 'Sur demande'}
                      </span>
                    </div>
                    <Button asChild className="w-full bg-slate-900 hover:bg-slate-800">
                      <Link to={`/property/${property.id}`}>
                        Voir les détails
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700">
              <Link to="/villas">Voir tous les biens</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Témoignages Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Ce que disent nos clients
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Découvrez les témoignages de nos clients satisfaits
            </p>
          </div>

          {testimonials.length > 0 ? (
            <>
              {/* Grille de 3 témoignages */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visibleTestimonials.map((t) => (
                  <Card key={t.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardContent className="p-6">
                      <Quote className="w-8 h-8 text-amber-200 mb-4" />
                      <div className="flex mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= t.note
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <p className="text-slate-600 mb-4 italic line-clamp-4">
                        "{t.commentaire}"
                      </p>
                      <div className="flex items-center gap-3 pt-4 border-t">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <span className="text-amber-700 font-semibold text-sm">
                            {t.nomClient?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <span className="font-semibold text-slate-900">{t.nomClient}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Navigation : flèches + indicateurs */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-6 mt-8">
                  <button
                    onClick={() => setTestimonialPage((prev) => (prev - 1 + totalPages) % totalPages)}
                    className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-amber-50 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-slate-700" />
                  </button>

                  <div className="flex gap-2">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setTestimonialPage(i)}
                        className={`w-3 h-3 rounded-full transition-colors ${
                          i === testimonialPage ? 'bg-amber-600' : 'bg-slate-300 hover:bg-slate-400'
                        }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={() => setTestimonialPage((prev) => (prev + 1) % totalPages)}
                    className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-amber-50 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-slate-700" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-slate-500 py-8">
              Aucun témoignage pour le moment. Soyez le premier à partager votre expérience !
            </div>
          )}

          <div className="text-center mt-10">
            <Button
              onClick={() => setShowTestimonialForm(true)}
              className="bg-amber-600 hover:bg-amber-700"
              size="lg"
            >
              Laisser un témoignage
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonial Form Dialog */}
      <Dialog open={showTestimonialForm} onOpenChange={setShowTestimonialForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Laisser un témoignage</DialogTitle>
            <DialogDescription>
              Partagez votre expérience avec PAM IMMO SERVICES
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTestimonialSubmit} className="space-y-4">
            <div>
              <Label htmlFor="testimonial-name">Votre nom</Label>
              <Input
                id="testimonial-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ex: Aminata Diallo"
                required
                className="mt-2"
              />
            </div>

            <div>
              <Label>Votre note</Label>
              <div className="flex gap-1 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= (hoverRating || formRating)
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-slate-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="testimonial-comment">Votre témoignage</Label>
              <Textarea
                id="testimonial-comment"
                value={formComment}
                onChange={(e) => setFormComment(e.target.value)}
                placeholder="Décrivez votre expérience avec nos services..."
                rows={4}
                required
                className="mt-2"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTestimonialForm(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={submitting}
              >
                {submitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Prêt à concrétiser votre projet immobilier ?</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-600 hover:bg-amber-700 text-lg px-8">
              <Link to="/contact">
                <Phone className="mr-2 w-5 h-5" />
                Nous appeler
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="bg-white/10 border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8">
              <Link to="/villas">Parcourir les biens</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
